/* eslint-disable @typescript-eslint/no-explicit-any */

"use client";
import React, { useEffect, useMemo, useState } from "react";
import Highcharts from "highcharts";
import Image from "next/image";
import HighchartsReact from "highcharts-react-official";
import { formatDate, getFundAgeInYears } from "@/utils/commonFunctions";
import { RollingNavReturns } from "@/type/rollingPageTypes";

type Periods = 1 | 3 | 5;

type ChartDataType = {
  schemeName: string | null;
  fundAge: string | null;
  chartData: RollingNavReturns[];
  activePeriod: Periods;
  onPeriodChange: (v: Periods) => void;
  isLoggedIn?: boolean;
  isFetching?: boolean;
};

const RollingReturnChart = React.memo(function RollingReturnChart({
  chartData,
  schemeName = "Period",
  fundAge,
  activePeriod,
  onPeriodChange,
  isLoggedIn,
  isFetching,
}: ChartDataType) {
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState<{ min: RollingNavReturns | null; max: RollingNavReturns | null }>({
    min: null,
    max: null,
  });

  // Helper to parse date without expensive toLocaleString
  const parseDateToTimestamp = (dateStr: string) => {
    if (!dateStr) return 0;
    const parts = dateStr.split("-");
    if (parts.length === 3 && parts[2].length === 4) {
      const [day, month, year] = parts;
      return new Date(`${year}-${month}-${day}T00:00:00+05:30`).getTime();
    }
    return new Date(dateStr).getTime();
  };

  const fundYears = getFundAgeInYears(fundAge);

  useEffect(() => {
    if (isFetching) {
      setIsLoading(true);
    }
  }, [isFetching, activePeriod, chartData]);

  useEffect(() => {
    if (!chartData || chartData.length === 0) {
      setStats({ min: null, max: null });
      return;
    }

    const timer = setTimeout(() => {
      let min = chartData[0];
      let max = chartData[0];

      for (const d of chartData) {
        if (d.returnPercentage < min.returnPercentage) min = d;
        if (d.returnPercentage > max.returnPercentage) max = d;
      }
      setStats({ min, max });
    }, 150);

    return () => clearTimeout(timer);
  }, [chartData]);

  const seriesData = useMemo(() => {
    const seen = new Set();

    return chartData
      .map((d) => {
        const x = parseDateToTimestamp(d.endDate);
        return {
          x,
          y: d.returnPercentage,
          start_date: d.startDate,
          end_date: d.endDate,
        };
      })
      .filter((point) => {
        if (seen.has(point.x)) return false;
        seen.add(point.x);
        return true;
      })
      .sort((a, b) => a.x - b.x);
  }, [chartData]);

  const options: Highcharts.Options = useMemo(
    () => ({
      chart: {
        type: "line",
        height: 180,
        events: {
          load: function () {
            setIsLoading(false);
          },
          redraw: function () {
            setIsLoading(false);
          },
        },
      },
      title: { text: undefined },
      xAxis: {
        type: "datetime",
        labels: {
          format: "{value:%b %Y}",
          style: { fontSize: "11px" },
          rotation: -45,
        },
        tickPixelInterval: 1,
      },

      yAxis: {
        title: { text: undefined },
        labels: {
          formatter: function () {
            return this.value + "%";
          },
        },
      },
      tooltip: {
        formatter: function (this: any) {
          return `
          <p class="flex items-center gap-1"><span class="truncate max-w-40 text-[#464646] font-medium text-xs shrink-0">${schemeName} </span><span class="shrink-0 text-[#949494]"> ${formatDate(this.point.options.start_date)} → ${formatDate(this.point.options.end_date)}</span><br/></p>
          <p class="flex items-center gap-1"><span class="line-clamp-1 max-w-40 text-[#464646] font-medium text-xs shrink-0">Return: </span><span class="shrink-0 ${Number(this.y) > 0 ? "text-primaryGreen" : "text-[#ff0000]"}">${Number(this.y)?.toFixed(2)}%</span> </p>
    `;
        },
        useHTML: true,
      },

      series: [
        {
          id: "main-series",
          type: "line",
          name: "CAGR",
          data: seriesData,
          color: "#1a51aa",
          turboThreshold: null,
          marker: {
            enabled: false,
          },
        },
      ],
      credits: { enabled: false },
      legend: { enabled: false },
    }),
    [seriesData, schemeName],
  );

  return (
    <div className="py-5 my-5 border-y border-[#9c9c9c] border-dashed">
      <p className="text-2sm sm:text-lg line-clamp-2 text-primaryBlack font-semibold mb-2">
        {isLoggedIn ? "Rolling Returns - Graph Analysis" : "Rolling Returns - Dummy Analysis"}
      </p>

      <div className="flex flex-col xs:flex-row items-center justify-between mb-5">
        <div className="flex gap-2 px-4 xs:px-0 shrink-0">
          {[1, 3, 5].map((yr) => {
            const isDisabled = yr >= fundYears;

            return (
              <button
                key={yr}
                disabled={isDisabled}
                onClick={() => onPeriodChange(yr as Periods)}
                className={`px-4 py-1 rounded-lg text-sm font-medium border ${!isLoggedIn ? "opacity-50 cursor-not-allowed" : ""} ${isDisabled ? "opacity-30 cursor-not-allowed" : ""} ${activePeriod === yr ? "bg-primaryBlue text-white border-primaryBlue" : "border-gray-300 text-gray-600 hover:bg-gray-100"}`}
              >
                {yr}Yr
              </button>
            );
          })}
        </div>

        {isLoggedIn && (
          <div className="flex flex-wrap justify-center gap-2 shrink-0">
            {stats.min && (
              <div className="px-2 py-1 rounded-lg border border-gray-300 bg-gray-50 text-2xs shrink-0 space-y-0.5">
                <div className="flex items-center gap-3">
                  <p className="text-gray-700 font-medium">Min :</p>
                  <p
                    className={`text-sm font-semibold ${stats.min.returnPercentage < 0 ? "text-red-700" : "text-[#2C841C]"}`}
                  >
                    {Number(stats.min.returnPercentage).toFixed(2)}%
                  </p>
                </div>
                <p className="text-xs text-gray-500">
                  {formatDate(stats.min.startDate, "dd-MM-yyyy")} → {formatDate(stats.min.endDate, "dd-MM-yyyy")}
                </p>
              </div>
            )}
            {stats.max && (
              <div className="px-2 py-1 rounded-lg border border-gray-300 bg-gray-50 text-2xs shrink-0 space-y-0.5">
                <div className="flex items-center gap-3">
                  <p className="text-gray-700 font-medium">Max :</p>
                  <p
                    className={`text-sm font-semibold ${stats.max.returnPercentage < 0 ? "text-red-700" : "text-[#2C841C]"}`}
                  >
                    {Number(stats.max.returnPercentage).toFixed(2)}%
                  </p>
                </div>
                <p className="text-xs text-gray-500">
                  {formatDate(stats.max.startDate, "dd-MM-yyyy")} → {formatDate(stats.max.endDate, "dd-MM-yyyy")}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="h-45 overflow-hidden">
        {isLoading && (
          <Image
            src="/next_assets/images/gif/chart_loading.gif"
            unoptimized
            alt="Loading Chart Animation"
            loading="eager"
            width={256}
            height={144}
            priority
            className="w-64 h-36 block mx-auto"
          />
        )}

        <HighchartsReact highcharts={Highcharts} options={options} />
      </div>

      <p className="text-xs text-[#6E6E73] font-medium mt-2 px-4">
        {isLoggedIn
          ? `*The rolling returns of ${schemeName} have been calculated based on the past ${fundAge}.`
          : `*The rolling returns of ${schemeName} are fake and for real login.`}
      </p>
    </div>
  );
});

export default RollingReturnChart;
